/**
 * Listing-channel reference data (T12, frontend-only). Single source for the
 * Listings tracker's channel order, labels, and the copy-modal tab set — pages
 * import these instead of inlining channel arrays. Mirrors the backend channel
 * catalog until a server-driven source replaces it.
 *
 * `CHANNELS` entries are `[key, name, short]`: full name for chips/labels, short
 * label for the dense tracker header. `COPY_CHANNELS` is the subset with
 * deterministic listing copy (the modal tabs) — Car-Part stays tracker-only
 * (#317), so it is intentionally absent. `CHANNEL_LABEL` maps a channel key to
 * its display name for copy-modal headers / mark-listed toasts.
 */

/** Tracker channel order, matching the table columns (Plane #101/#103). */
const CHANNELS = [
  ['own', 'Own site', 'Own'],
  ['ebay', 'eBay', 'eBay'],
  ['facebook', 'Facebook', 'FB'],
  ['craigslist', 'Craigslist', 'Craigsl.'],
  ['carpart', 'Car-Part', 'Car-Part'],
];

/** Channels with deterministic listing copy (the modal tabs). */
const COPY_CHANNELS = [
  ['ebay', 'eBay'],
  ['facebook', 'Facebook'],
  ['craigslist', 'Craigslist'],
];

/** Channel key → display name (copy-modal header, mark-listed toast). */
const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map(([key, name]) => [key, name]));

/** Channel key → short header label for the dense tracker columns. */
const CHANNEL_SHORT = Object.fromEntries(CHANNELS.map(([key, , short]) => [key, short]));

/**
 * Sale-channel reference data (T12). The Sales area's channel set extends the
 * tracker channels with `other` (manual / part-less revenue, scrap hauls) and
 * matches the backend `SaleChannel`. Kept distinct from `CHANNELS` so the
 * Listings tracker is unaffected. `SALE_CHANNELS` entries are `[key, label]`
 * (filter-bar order); `SALE_CHANNEL_PILLS` is the record-form pill order (M7).
 */
const SALE_CHANNELS = [
  ['own', 'Own site'],
  ['ebay', 'eBay'],
  ['facebook', 'Facebook'],
  ['craigslist', 'Craigslist'],
  ['carpart', 'Car-Part'],
  ['other', 'Other'],
];

/** Record-form channel pill order (single-select, optional) — matches the M7 modal. */
const SALE_CHANNEL_PILLS = ['ebay', 'facebook', 'craigslist', 'own', 'carpart', 'other'];

/** Sale-channel key → display name (table cell, chips, record/edit selects). */
const SALE_CHANNEL_LABEL = Object.fromEntries(SALE_CHANNELS.map(([key, label]) => [key, label]));

export {
  CHANNEL_LABEL,
  CHANNEL_SHORT,
  CHANNELS,
  COPY_CHANNELS,
  SALE_CHANNEL_LABEL,
  SALE_CHANNEL_PILLS,
  SALE_CHANNELS,
};
